import React, { useState, useRef, useEffect, memo} from 'react';
import * as THREE from 'three';
import { NURBSCurve } from 'three/addons/curves/NURBSCurve.js';
import { NURBSSurface } from 'three/addons/curves/NURBSSurface.js';
import { Text } from '@react-three/drei';
import { toast } from 'react-toastify';

import { TubeCurve } from '../shape/Spline';
import { Points } from '../shape/Point';

import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { Geometry, Base, Subtraction, Addition } from '@react-three/csg'


import {
  visualize,
  sweepOneProfileCurveAlongTwoTraceCurves,
  makeCutObjectSi,
  makeCutObjectMl,
  makeCutObjectAp,
  cutTest,
  cutTwoShapes
} from './utilsOcct.js'

import {
  calcCirclePoints,
  calculateCurveCenter,
  calculateCurveLength,
  offsetCurve,
  intersectionCurvePlane,
  calculateIntersectionPoints,
  lineSegmentsIntersect,
  findClosestToZeroY,
  calcIntersectionMedialCurves,
  findClosestPointOnPoints,
  intersectionPointsPlane,
  remapCurve,
  makeCurveClockwise,
  alignSeamPoints,
  createLoftSurfaceFrom2Curves,
  shatterCurvePoints,
  findIntersectionPoints,
  sortPoints
} from '../shape/utils';


/**
 * Calculates the curve points for the greater tronchanter based on the given parameters.
 * @param {Array<THREE.Vector3>} crossPoints - The cross points.
 * @param {THREE.Vector3} centroidBaseshape - The centroid of the base shape.
 * @param {THREE.Vector3} greaterTrochanter - The greater trochanter.
 * @param {number} p0Y - The y-coordinate of the first point.
 * @param {number} p1Y - The y-coordinate of the second point.
 * @param {number} p2Y - The y-coordinate of the third point.
 * @returns {Array<THREE.Vector3>} The curve points.
 */
function getGreaterTronchanterCurvePoints(crossPoints, centroidBaseshape, greaterTrochanter, p0Y, p1Y, p2Y) {
  var points = [];

  const intersectionPoint = findClosestPointOnPoints(crossPoints, new THREE.Vector3(greaterTrochanter?.x, 0, 0).add(centroidBaseshape));

  points = [
    new THREE.Vector3(intersectionPoint.point?.x, intersectionPoint.point?.y + p0Y, intersectionPoint.point?.z),
    new THREE.Vector3(intersectionPoint.point?.x, intersectionPoint.point?.y + p1Y, intersectionPoint.point?.z),
    new THREE.Vector3(intersectionPoint.point?.x, intersectionPoint.point?.y + p2Y, intersectionPoint.point?.z),
    new THREE.Vector3(intersectionPoint.point?.x, 0, intersectionPoint.point?.z),
  ];

  return points;
}

function getMedialCurvePoints(crossPoints, centroidBaseshape) {
  var points = [];

  var intersections = intersectionPointsPlane(crossPoints, new THREE.Vector3(0, 0, 1), 0); // 0: medial, 1: lateral point
  const mlDiameter = intersections[0].distanceTo(intersections[1]);

  if (intersections[0].x > intersections[1].x) {
    intersections.reverse();
  }

  points = [
    intersections[0],
    new THREE.Vector3(intersections[0].x, intersections[0].y + 5, intersections[0].z),
    new THREE.Vector3(intersections[0].x - 10, intersections[0].y + 5, intersections[0].z),
  ];



  return { points: points, mlDiameter: mlDiameter };
}

function modifyPoints(points, p0X, p1X, p2X) {
  var newPoints = [];
  for (let i = 0; i < points.length; i++) {
    var newPoint = new THREE.Vector3(points[i].x, points[i].y, 0);
    if (i == 0) {
      newPoint.x += p0X;
    } else if (i == 1) {
      newPoint.x += p1X;
    } else if (i == 2) {
      newPoint.x += p2X;
    }
    newPoints.push(newPoint);
  }
  return newPoints;
}


function getNurbsCurve(points) {
  const nurbsDegree = 2;
  const nurbsKnots = [];
  const nurbsControlPoints = [];

  for (let i = 0; i <= nurbsDegree; i++) {
    nurbsKnots.push(0);
  }

  for (let i = 0, j = points.length; i < j; i++) {
    nurbsControlPoints.push(new THREE.Vector4(points[i].x, points[i].y, points[i].z, 1));

    const knot = (i + 1) / (j - nurbsDegree);
    nurbsKnots.push(THREE.MathUtils.clamp(knot, 0, 1));
  };

  const nurbsCurve = new NURBSCurve(nurbsDegree, nurbsKnots, nurbsControlPoints);
  return nurbsCurve;
}

function getBaseshapeMlDividedPoints(crossPoints, mlDiameter) {
  const divMl = 12;
  const divLenMl = mlDiameter / divMl;
  var intersections = [];
  var tempIntersections = [];

  // get intersection points of medial curve and plane, divided 13 points
  for (let i = 0; i <= divMl; i++) {
    tempIntersections = intersectionPointsPlane(crossPoints, new THREE.Vector3(1, 0, 0), -divLenMl * (divMl - i)); // 0: medial, 1: lateral point
    // sort intersections based on z value, ap direction
    if (tempIntersections.length !== 0) {
      const sortedIndexes = tempIntersections.map((value, index) => {
        value = value.z;
        return { value, index };
      })
        .sort((a, b) => a.value - b.value)
        .map(item => item.index);

      intersections.push([
        tempIntersections[sortedIndexes[0]], // anterior intersection point
        tempIntersections[sortedIndexes[sortedIndexes.length - 1]] // posterior intersection point
      ]);
    } else {
      intersections.push([]);
    }
  }

  return intersections;
}

function getBackCurvePoints(intersections, centroidBaseshape, p3Y, p3CenterVecAmplify, p4Y, p4CenterVecAmplify, p5Y, p5CenterVecAmplify) {
  var points = [];
  var tempPoints = [];
  var temp = [];

  points = [
    new THREE.Vector3(...intersections[3][0]),
    new THREE.Vector3(intersections[3][0].x + 10, intersections[3][0].y + 50 + p3Y, intersections[3][0].z - 10),
  ];

  const diff = points[0].clone().sub(points[1])

  // move top point to baseshape center
  var vecToCenter = new THREE.Vector3(points[1].x, 0, points[1].z).sub(centroidBaseshape).normalize();
  vecToCenter = vecToCenter.multiplyScalar(p3CenterVecAmplify);

  // calculate middle point, calculation process is from RhinoGH, not checked detail.
  temp = new THREE.Vector3(points[0].x + 5, points[0].y, points[0].z - 5);
  temp = temp.add(points[1]);

  // middle point 0
  var middlePoint0 = points[0].clone().sub(diff.clone().multiplyScalar(2 / 3));
  middlePoint0 = new THREE.Vector3(middlePoint0.x, middlePoint0.y + p4Y, middlePoint0.z);
  var vecToCenterMiddle0 = new THREE.Vector3(middlePoint0.x, 0, middlePoint0.z).sub(centroidBaseshape).normalize();
  vecToCenterMiddle0 = vecToCenterMiddle0.multiplyScalar(p4CenterVecAmplify);

  // middle point 1
  var middlePoint1 = points[0].clone().sub(diff.clone().multiplyScalar(1 / 3));
  middlePoint1 = new THREE.Vector3(middlePoint1.x, middlePoint1.y + p5Y, middlePoint1.z);
  var vecToCenterMiddle1 = new THREE.Vector3(middlePoint1.x, 0, middlePoint1.z).sub(centroidBaseshape).normalize();
  vecToCenterMiddle1 = vecToCenterMiddle1.multiplyScalar(p5CenterVecAmplify);

  points = [
    points[0],
    middlePoint1.sub(vecToCenterMiddle1),
    middlePoint0.sub(vecToCenterMiddle0),
    points[1].sub(vecToCenter),
  ]

  return points;
}

function getLateralFrontCurvePoints(intersections, centroidBaseshape, p6Y, p6Z, p7Y, p7Z) {
  const points = [
    intersections[4][1],
    new THREE.Vector3(intersections[4][1].x, intersections[4][1].y + p6Y, intersections[4][1].z + p6Z),
    new THREE.Vector3(intersections[4][1].x, intersections[4][1].y + p7Y, intersections[4][1].z + 30 + p7Z),
  ]

  return points;
}

function getMedialFrontCurvePoints(intersections, centroidBaseshape, p8Y, p8Z, p9Y, p9Z) {
  const points = [
    intersections[11][1],
    new THREE.Vector3(intersections[11][1].x, intersections[11][1].y + p8Y, intersections[11][1].z + p8Z),
    new THREE.Vector3(intersections[11][1].x, intersections[11][1].y + p9Y, intersections[11][1].z + 50 + p9Z),
  ]

  return points;
}

function getMedialBackCurvePoints(intersections, centroidBaseshape, p10Y, p10Z, p11Y, p11Z) {
  var points = [];

  points = [
    intersections[11][0],
    new THREE.Vector3(intersections[11][0].x, intersections[11][0].y + 3 + p10Y, intersections[11][0].z - 10 + p10Z),
    new THREE.Vector3(intersections[11][0].x, intersections[11][0].y + 3 + p11Y, intersections[11][0].z - 50 + p11Z),
  ]

  return points;

}

function getMedialBackCurveEndPoints(intersections, centroidBaseshape, p12Y, p12Z, p13Y, p13Z) {
  var points = [];

  points = [
    intersections[4][0],
    new THREE.Vector3(intersections[4][0].x, intersections[4][0].y + 3 + p12Y, intersections[4][0].z - 10 + p13Z),
    new THREE.Vector3(intersections[4][0].x, intersections[4][0].y + 3 + p12Y, intersections[4][0].z - 50 + p13Z),
  ]

  return points;

}


function remapSurface(geom, basePoints, centroidBaseshape) {
  const objects = [];
  const objectsSurface = [];
  var key = 0;
  const tempArray = [];
  const tempFirst = [];
  const tempSecond = []

  const angleStep = 5;
  const center = centroidBaseshape;

  for (let angle = 0; angle < 180; angle += angleStep) {
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    const plane = new THREE.Plane(normal, -normal.dot(center));

    var intersectionPoints = findIntersectionPoints(geom, plane);
    //console.log(intersectionPoints);
    intersectionPoints = sortPoints(intersectionPoints);

    const distances = [];
    for (let i = 0; i < intersectionPoints.length - 1; i++) { // -1は最後の要素（直線Aの最後）と最初の要素（直線Bの最初）を除くため
      const point = intersectionPoints[i];
      const nextPoint = intersectionPoints[(i + 1) % intersectionPoints.length];
      distances.push(point.distanceTo(nextPoint));
    }


    const max = Math.max(...distances);
    const maxIndex = distances.indexOf(max);

    var tempFirstHalf = sortPoints(intersectionPoints.slice(0, maxIndex + 1));
    // remove duplicate points
    var firstHalf = tempFirstHalf.reduce((accumulator, currentValue) => {
      if (!accumulator.some(p => p.equals(currentValue))) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []);

    var tempSecondHalf = sortPoints(intersectionPoints.slice(maxIndex + 1, intersectionPoints.length));
    // remove duplicate points
    var secondHalf = tempSecondHalf.reduce((accumulator, currentValue) => {
      if (!accumulator.some(p => p.equals(currentValue))) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []);


    // sort points by y, TODO: but temporary solution
    firstHalf.sort((a, b) => {
      //return a.y - b.y;
      return a.distanceTo(center) - b.distanceTo(center);
    });
    secondHalf.sort((a, b) => {
      //return a.y - b.y;
      return a.distanceTo(center) - b.distanceTo(center);
    });

    //const refPoint = new THREE.Vector3(Math.sin(radians), 0, Math.cos(radians)).add(normal.multiplyScalar(50));

    var refPoint = normal.multiplyScalar(50);

    // // Z軸周りに90度回転する行列を作成
    var rotationMatrix = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
    // // 回転行列をベクトルに適用
    refPoint.applyMatrix4(rotationMatrix);
    refPoint = refPoint.add(center);

    if (firstHalf[0].distanceTo(refPoint) > secondHalf[0].distanceTo(refPoint)) {
      // swap
      const temp = firstHalf;
      firstHalf = secondHalf;
      secondHalf = temp;
    }

    tempArray.push(firstHalf);
    tempSecond.push(secondHalf)
  }

  const div = 10;

  var tempWarpPoints = tempArray.concat(tempSecond, tempFirst)

  /*tempWarpPoints = tempWarpPoints.map((points) => {
    // sort by Y if maxY > 20 (lateral wall mesh is strange), TODO: this is not smart way
    const maxY = Math.max(...points.map((point) => point.y));
    if (maxY > 30) {
      // sort by Y
      points.sort((a, b) => {
        return a.y - b.y;
      })
    } else {
      points.sort((a, b) => {
        return a.distanceTo(centroidBaseshape) - b.distanceTo(centroidBaseshape);
      })
    }

    if (points[0].distanceTo(centroidBaseshape) > points[points.length-1].distanceTo(centroidBaseshape)) {
      points.reverse();
    }

    return points;
    //return sortByMaxDistanceFromCenter(points, centroidBaseshape);
  })
  */

  tempWarpPoints


  var warpPoints = tempWarpPoints.map((points) => {
    return new THREE.CatmullRomCurve3(points).getSpacedPoints(div);
  })

  const lengthes = warpPoints.map((points) => {
    return new THREE.CatmullRomCurve3(points).getLength();
  })

  // find 
  const indicesToRemove = lengthes.reduce((accumulator, currentValue, index) => {
    if (currentValue <= 3) {
      accumulator.push(index);
    }
    return accumulator;
  }, []);

  // 配列から要素を削除する
  for (let i = indicesToRemove.length - 1; i >= 0; i--) {
    warpPoints.splice(indicesToRemove[i], 1);
  }

  warpPoints.push(warpPoints[0]); // to close the loop

  // replace the first point with the basepoitns
  for (let i = 0; i < warpPoints.length; i++) {
    warpPoints[i][0] = findClosestPointOnPoints(basePoints, warpPoints[i][0]).point;
  }

  // Weft points
  // weft curve points
  const weftPoints = [];
  for (let i = 0; i < div + 1; i++) {
    weftPoints.push(warpPoints.map((points) => points[i]));
  }


  ///////// Simple loft with three.js /////////////////////////////////////////////////
  for (let i = 0; i < weftPoints.length - 1; i++) {
    const loftPoints0 = makeCurveClockwise(weftPoints[i]);
    const loftPoints1 = alignSeamPoints(loftPoints0, makeCurveClockwise(weftPoints[i + 1]));
    const loftCurve0 = new THREE.CatmullRomCurve3(loftPoints0, true);
    const loftCurve1 = new THREE.CatmullRomCurve3(loftPoints1, true);

    const loftSurface = createLoftSurfaceFrom2Curves(loftCurve0, loftCurve1, 150, 10, 'white', THREE.DoubleSide);

    objectsSurface.push(
      <primitive key={key} object={loftSurface} />
    );
    key += 1;
  }
  if (objectsSurface.length !== 0) {
    var temp = objectsSurface.map((obj) => obj.props.object);
  }


  ////////// Trimming test /////////////////////////////////////////////////
  const geomObject = objectsSurface.map((mesh) => {
    return mesh.props.object.geometry;
  })

  const objectsTemp = [];
  var geomTrim = BufferGeometryUtils.mergeGeometries(geomObject);

  return geomTrim;
}

export const MUpperSurface = memo(({
  oc,
  crossPoints,
  centroidBaseshape,
  lowerSurface,
  upperCurvePointsAll,
  idxPointForm,
  callback,
  color,
  colorHigh,
  colorSub,
}) => {
  const ref = useRef();
  var key = 0;
  var objectsSurface = [];
  const objectsSurfaceRemap = [];
  var idxSurface = [];
  var success = false;
  var shapeSurfaces = new Array(6).fill(null);
  const [objects, setObjects] = useState([]);
  const [objectsSub, setObjectsSub] = useState([]);
  const [objectsLower, setObjectsLower] = useState([]);


  // Set which surface to show
  if (idxPointForm === 0 || idxPointForm === 1 || idxPointForm === 2) {
    idxSurface = [0, 6];
  } else if (idxPointForm === 3 || idxPointForm === 4 || idxPointForm === 5) {
    idxSurface = [0, 1];
  } else if (idxPointForm === 6 || idxPointForm === 7) {
    idxSurface = [5, 6];
  } else if (idxPointForm === 8 || idxPointForm === 9) {
    idxSurface = [4, 5];
  } else if (idxPointForm === 10 || idxPointForm === 11) {
    idxSurface = [2, 3];
  } else if (idxPointForm === 12 || idxPointForm === 13) {
    idxSurface = [1, 2];
  } else {
    idxSurface = [0, 1, 2, 3, 4, 5, 6];
  }

  useEffect(() => {
    if (upperCurvePointsAll == null) {
      return null;
    }

    // calc bottom center
    const bottomCenter = new THREE.Vector3();
    for (let i = 0; i < crossPoints[0].length; i++) {
      bottomCenter.add(crossPoints[0][i]);
    }
    bottomCenter.divideScalar(crossPoints[0].length);


    //const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
    const material = new THREE.MeshLambertMaterial({ color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide });

    // Make syrface with opencascade //////////////////////////////////////////////
    for (let i = 0; i < upperCurvePointsAll.length; i++) {
      if (idxSurface.indexOf(i) === -1) {
        continue
      }

      var guideCurvePointsSurface0 = upperCurvePointsAll[i];
      var guideCurvePointsSurface1 = upperCurvePointsAll[(i + 1) % upperCurvePointsAll.length];
      //var guideCurve0PointsSurface0 = new THREE.CatmullRomCurve3(upperCurvePointsAll[i]).getSpacedPoints(200);
      //var guideCurve1PointsSurface0 = new THREE.CatmullRomCurve3(upperCurvePointsAll[(i+1)%upperCurvePointsAll.length]).getSpacedPoints(200);

      var temp = crossPoints[crossPoints.length - 1].map((point) => {
        return new THREE.Vector3(point.x, point.y, point.z);
      });
      const baseshape = new THREE.CatmullRomCurve3(temp)
      const baseshapePoints = baseshape.getSpacedPoints(200);

      var tempPoints0 = findClosestPointOnPoints(baseshapePoints, guideCurvePointsSurface0[0]);
      var tempPoints1 = findClosestPointOnPoints(baseshapePoints, guideCurvePointsSurface1[0]);
      var shatterCurvePointsSurface0 = shatterCurvePoints(baseshapePoints, tempPoints0.idx, tempPoints1.idx)

      var profilePointsSurface0 = shatterCurvePointsSurface0[0];

      var profilePoints = [
        guideCurvePointsSurface0[0],
        profilePointsSurface0[Math.floor(profilePointsSurface0.length / 2)],
        //profilePointsSurface0[Math.floor(profilePointsSurface0.length/4*2)],
        //profilePointsSurface0[Math.floor(profilePointsSurface0.length/3*2)],
        guideCurvePointsSurface1[0]
      ];


      //var profilePoints = profilePointsSurface0.slice(1, array.length - 1);
      //profilePoints.push(profilePointsSurface0[0])
      //profilePoints.push(profilePointsSurface0[profilePointsSurface0.length-1])

      /*
      objectsSub.push(<TubeCurve 
        key={key} 
        vpoints={profilePoints} 
        radius={5} 
        color={color}
        />)
      key += 1;
      */

      try {
        var shapeSurface0 = sweepOneProfileCurveAlongTwoTraceCurves(oc, profilePoints, guideCurvePointsSurface0, guideCurvePointsSurface1);
        shapeSurfaces[i] = shapeSurface0;
        console.log(shapeSurface0)
        if (shapeSurface0 !== null) {
          success = true;
        } else {
          success = false;
        }
      } catch (e) {
        console.log(e);
      }

      if (shapeSurface0) {
        let geometries = visualize(oc, shapeSurface0)

        geometries.forEach(geometry => {
          const mesh = new THREE.Mesh(geometry, material);
          objectsSurface.push(
            <primitive key={key} object={mesh} />
          );
          key += 1;
        });
      }
    }

    const lowerSurfaceMesh = new THREE.Mesh(lowerSurface, material);
    //objectsLower.push(
    //  <primitive key={key} object={lowerSurfaceMesh} />
    //);
    //key += 1;
    //setObjectsLower(objectsLower);



    if (success) {
      /////////////// Remap upper surface ////////////////////////
      // Merge upper geometries
      if (idxPointForm === -1) {
        const geometries = objectsSurface.map((obj) => {
          return obj.props.object.geometry;
        })
        var geom = BufferGeometryUtils.mergeGeometries(geometries);
        const geomRemap = remapSurface(geom, crossPoints[crossPoints.length - 1], centroidBaseshape)
        const material = new THREE.MeshLambertMaterial({ color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide });
        const upperSurfaceMesh = new THREE.Mesh(geomRemap, material);
        objectsSurfaceRemap.push(
          <primitive key={key} object={upperSurfaceMesh} />
        );
        key += 1;




        /*
        // Merge lower and upper //////////////////////////////////////////
        // get warp points to merge lower and upper surface 
        var lowerWarpPointsAll = getContourPointsCircumAll(lowerSurfaceMesh, bottomCenter, 10, -10000);
        var upperWarpPointsAll = getContourPointsCircumAll(upperSurfaceMesh, bottomCenter, 10, -10000);

        upperWarpPointsAll = upperWarpPointsAll.map((points) => {
          // sort by Y if maxY > 20 (lateral wall mesh is strange), TODO: this is not smart way
          const maxY = Math.max(...points.map((point) => point.y));
          if (maxY > 30) {
            // sort by Y
            points.sort((a, b) => {
              return a.y - b.y;
            })
          } else {
            points.sort((a, b) => {
              return a.distanceTo(centroidBaseshape) - b.distanceTo(centroidBaseshape);
            })
          }
          if (points[0].distanceTo(centroidBaseshape) > points[points.length-1].distanceTo(centroidBaseshape)) {
            points.reverse();
          }
    
          return points;
        })

        
        // if (lowerWarpPointsAll.length > 0) {
        //   for (let i=0; i<lowerWarpPointsAll.length; i++) {
        //     objectsSub.push(<TubeCurve 
        //       key={key} 
        //       vpoints={lowerWarpPointsAll[i]} 
        //       radius={0.5}
        //       color={colorHigh}
        //       />
        //     );
        //     key += 1;
        //   }
        // }
    
        // if (upperWarpPointsAll.length > 0) {
        //   for (let i=0; i<upperWarpPointsAll.length; i++) {
        //     objectsSub.push(<TubeCurve 
        //       key={key} 
        //       vpoints={upperWarpPointsAll[i]} 
        //       radius={0.5}
        //       color={colorHigh}
        //       />
        //     );
        //     key += 1;
        //   }
        // }
        

        // merge lower and upper warp points
        var wrapPointsAll = [];
        for (let i=0; i<lowerWarpPointsAll.length; i++) {
          wrapPointsAll.push(lowerWarpPointsAll[i].concat(upperWarpPointsAll[i]))
        }
        wrapPointsAll.push(wrapPointsAll[0])

        const div = 30;

        var divWarpPointsAll = [];
        divWarpPointsAll = wrapPointsAll.map((points) => {
          const temp = new THREE.CatmullRomCurve3(points);
          return temp.getSpacedPoints(div);
        });


        
        // if (divWarpPointsAll.length > 0) {
        //   for (let i=0; i<divWarpPointsAll.length; i++) {
        //     objectsSub.push(<TubeCurve
        //       key={key}
        //       vpoints={divWarpPointsAll[i]}
        //       radius={0.5}
        //       color={colorHigh}
        //     />
        //     );
        //     key += 1;
        //   }
        // }
        

        // loft whole surface
        var divWeftPointsAll = [];
        for (let i = 0; i < div+1; i++) {
          const divWeftPoints = divWarpPointsAll.map((points) => points[i]);
          divWeftPointsAll.push(divWeftPoints);
        }
        
        divWeftPointsAll = divWeftPointsAll.map((points) => {
          return makeCurveClockwise(points);
        });

        
        // if (divWeftPointsAll.length > 0) {
        //   for (let i=0; i<divWeftPointsAll.length; i++) {
        //     objectsSub.push(<TubeCurve
        //       key={key}
        //       vpoints={divWeftPointsAll[i]}
        //       radius={0.5}
        //       color={colorHigh}
        //     />
        //     );
        //     key += 1;
        //   }
        // }
        
        
        for (let i = 0; i < divWeftPointsAll.length-1; i++) {
          var loftPoints0 = divWeftPointsAll[i];
          var loftPoints1 = alignSeamPoints(loftPoints0, divWeftPointsAll[i+1]);
          var loftCurve0 = new THREE.CatmullRomCurve3(loftPoints0, true);
          var loftCurve1 = new THREE.CatmullRomCurve3(loftPoints1, true);

          var loftSurface = createLoftSurfaceFrom2Curves(loftCurve0, loftCurve1, 150, 10, color);

          objectsSurfaceRemap.push(
            <primitive key={key} object={loftSurface} />
          );
          key += 1;
        }
        */





        setObjects(objectsSurfaceRemap);
        setObjectsSub(objectsSub)
        callback(geomRemap)
        //callback(loftSurface.geometry)
      } else {
        setObjects(objectsSurface);
      }
    }
  }, [upperCurvePointsAll, lowerSurface]);

  return objects.length !== 0 ? (
    <>
      {objects}
      {objectsSub}
      {/*
      {objectsLower}
      */}
    </>
  ) : null;
});


export const MUpperSurfaceSi = memo(({
  oc,
  upperSurface,
  pointsTrimSi,
  offsetPointsSi,
  callback,
  color,
  colorHigh,
  colorSub
}) => {
  const [objects, setObjects] = useState([]);
  const [objectsTrim2, setObjectsTrim2] = useState([]);
  const objectsTrim = [];
  const objectsSurface = [];
  //var shapeSurfaces = new Array(6).fill(null);
  var key = 0;
  var cuttedSurface = null;
  var geomTrim = null;
  var geomExtrude = null;

  var ref = useRef();

  useEffect(() => {
    if (upperSurface) {
      try {
        // Trim surface with opencascade from SI view //////////////////////////////////////////////
        if (pointsTrimSi == null) {
          return null;
        }

        console.log('MUpperSurfaceSi')

        var points = pointsTrimSi;

        var tempCutShape = makeCutObjectSi(oc, points);
        const cutShape = tempCutShape.shape

        if (cutShape) {
          /*
          geomTrim = visualize(oc, cutShape)

          geomTrim.forEach(geometry => {
            const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
            const mesh = new THREE.Mesh(geometry, material);
            objectsTrim.push(<primitive key={key} object={mesh} />)
            key += 1;
          });

          geomTrim = objectsTrim.map((obj) => {
            return obj.props.object.geometry;
          })
          geomTrim = BufferGeometryUtils.mergeGeometries(geomTrim);
          const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
          const meshTrim = new THREE.Mesh(geomTrim, material);
          */

          const shape = new THREE.Shape();
          shape.moveTo(tempCutShape.points[0].x, tempCutShape.points[0].z)
          for (let i = 1; i < tempCutShape.points.length; i++) {
            shape.lineTo(tempCutShape.points[i].x, tempCutShape.points[i].z);
          }

          const extrudeSettings = {
            steps: 1,
            depth: 200,
          };
          const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
          geomExtrude = new THREE.ExtrudeGeometry(shape, extrudeSettings);
          geomExtrude.rotateX(Math.PI * 0.5);
          geomExtrude.translate(0, extrudeSettings.depth / 2, 0);
          const meshExtrude = new THREE.Mesh(geomExtrude, material);
          //objectsSurface.push(
          //  <primitive key={key} object={meshExtrude} />
          //);
          //key += 1;


          /*
          const mesh = new THREE.Mesh(upperSurface, material);
          console.log(mesh)
          
          const subObj = CSG.subtract(mesh, meshExtrude);
          console.log(subObj)
          objectsSurface.push(
            <primitive key={key} object={subObj} />
          );
          key += 1;
          */

          /*
          var geomTrimmed = trimCsg(upperSurface, geomExtrude)
          console.log(geomTrimmed)
          //console.log(geomTrimmed.props.children[0].geometry)
          //geomTrimmed = geomTrimmed.props.children[0]
          //const mesh = new THREE.Mesh(geomTrimmed, material);
          //console.log(mesh)
          objectsSurface.push(
            <primitive key={key} object={geomTrimmed} />
          );
          key += 1;
          */
        }

        toast.info('Finish trimming.')
      } catch (e) {
        console.log(e);
      }
    }

    //setObjects(objectsSurface);
    setObjects(upperSurface);
    setObjectsTrim2(geomExtrude);
    //callback(cuttedSurface);
    console.log(upperSurface)
    console.log(ref.current.geometry)

    callback(ref.current.geometry);

  }, [upperSurface]);

  /*
  return objects.length !== 0 ? (
    <>
      {objects}
    </>
  ) : null;
  */

  return (
    <mesh >
      <Geometry ref={ref} computeVertexNormals>
        <Base name='base' geometry={upperSurface} scale={[1, 1, 1]} />
        <Subtraction name='sub' geometry={objectsTrim2} scale={[1, 1, 1]} />
      </Geometry>
      {/*
      <meshStandardMaterial color={color} transparent={true} opacity={0.4} side={THREE.DoubleSide}/>
      */}
      <meshLambertMaterial color={color} transparent={true} opacity={0.6} side={THREE.DoubleSide} />
    </mesh>
  )

});




export const MUpperSurfaceMl = memo(({
  oc,
  upperSurface,
  pointsTrim,
  callback,
  color,
  colorHigh,
  colorSub
}) => {
  const [objects, setObjects] = useState([]);
  const [objectsTrim, setObjectsTrim] = useState([]);
  const objectsSurface = [];
  var shapeSurfaces = new Array(6).fill(null);
  var key = 0;
  var cuttedSurface
  var geomTrim = null;
  var geomExtrude = null;

  var ref = useRef();

  console.log('MUpperSurfaceMl')
  useEffect(() => {
    /*
    if (upperSurface) {
      let positions = upperSurface.attributes.position.array;
      //let indices = upperSurface.index.array; // no need for non-index buffergeometry

      let newPositions = [];
      //let newIndices = [];
      let mapping = [];
      let j = 0;
      let minX = 0
      let cnt = 0

      for(let i = 0; i < positions.length; i += 3) {
        minX = Math.min(minX, positions[i])
      }

      console.log(upperSurface)
      console.log(upperSurface.attributes.position.array[189405])

      if (upperSurface.index === null) {
        console.log("This BufferGeometry is non-indexed.");
      } else {
        console.log("This BufferGeometry is indexed.");
      }

      ///*
      // x < 0の頂点を削除
      // 非インデックス化された BufferGeometry では、3つの連続する頂点が1つの面（三角形）を形成します。それぞれの頂点は3つの座標情報（x, y, z）を持つため、1つの面を表現するためには配列の9つの要素（= 3頂点 × 3座標）が必要となります。
      const offset = minX + 0.001;
      for(let i = 0; i < positions.length; i += 9) {
        //console.log(positions[189405])
        //if (i >= positions.length-90) {
        if(positions[i] <= offset && positions[i+3] <= offset && positions[i+6] <= offset) {
        //if(positions[i] <= offset) {
          console.log(i)
          console.log(positions[i], positions[i+1], positions[i+2])
          console.log(positions[i+3], positions[i+4], positions[i+5])
          console.log(positions[i+6], positions[i+7], positions[i+8])

        //   if (cnt < 180) {
        //     newPositions.push(
        //       positions[i], positions[i+1], positions[i+2],
        //       positions[i+3], positions[i+4], positions[i+5],
        //       positions[i+6], positions[i+7], positions[i+8]
        //     );
        //   }
          // newPositions.push(
          //       positions[i], positions[i+1], positions[i+2],
          //       positions[i+3], positions[i+4], positions[i+5],
          //       positions[i+6], positions[i+7], positions[i+8]
          //     );

        //   cnt += 1
        } else {
          newPositions.push(
            positions[i], positions[i+1], positions[i+2],
            positions[i+3], positions[i+4], positions[i+5],
            positions[i+6], positions[i+7], positions[i+8]
          );
        }
      }
      console.log(cnt)
      console.log(minX)
      console.log(upperSurface)

      // no need for non-index buffergeometry
      // // 削除した頂点を参照する面を削除
      // for(let i = 0; i < indices.length; i += 3) {
      //   if(mapping[indices[i]] !== undefined &&
      //     mapping[indices[i+1]] !== undefined &&
      //     mapping[indices[i+2]] !== undefined) {
      //     newIndices.push(mapping[indices[i]], mapping[indices[i+1]], mapping[indices[i+2]]);
      //   }
      // }
      //upperSurface.toNonIndexed()
      upperSurface.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
      
      //upperSurface.setIndex(newIndices);  // no need for non-index buffergeometry
      //upperSurface.computeVertexNormals();


      if (upperSurface.index === null) {
        console.log("This BufferGeometry is non-indexed.");
      } else {
        console.log("This BufferGeometry is indexed.");
      }
    }
    */

    if (upperSurface) {
      try {
        // Trim surface with opencascade from ML view //////////////////////////////////////////////
        if (pointsTrim == null) {
          return null;
        }

        console.log('MUpperSurfaceMl')
        const curve = new THREE.CatmullRomCurve3(pointsTrim, false, "catmullrom");
        const points = curve.getSpacedPoints(200);
        const tempCutShape = makeCutObjectMl(oc, points);

        const shape = new THREE.Shape();
        shape.moveTo(tempCutShape.points[0].y, tempCutShape.points[0].z)
        for (let i = 1; i < tempCutShape.points.length; i++) {
          shape.lineTo(tempCutShape.points[i].y, tempCutShape.points[i].z);
        }

        const extrudeSettings = {
          steps: 1,
          depth: 600,
        };

        geomExtrude = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        geomExtrude.rotateZ(Math.PI * 0.5);
        geomExtrude.rotateY(Math.PI * 0.5);
        geomExtrude.translate(-extrudeSettings.depth / 2, 0, 0);
        const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        const meshExtrude = new THREE.Mesh(upperSurface, material);

        //objectsSurface.push(
        //  <primitive key={key} object={meshExtrude} />
        //);
        //key += 1;

        toast.info('Finish trimming.')
      } catch (e) {
        console.log(e);
        //toast.error('Fail trimming.')
      }
    }
    setObjects(upperSurface);
    setObjectsTrim(geomExtrude);
    callback(ref.current.geometry);
  }, [upperSurface]);

  /*
  return objects.length !== 0 ? (
    <>
      {objects}
    </>
  ) : null;
  */

  return (
    <mesh >
      <Geometry ref={ref} computeVertexNormals>
        <Base name='base' geometry={objects} scale={[1, 1, 1]} />
        <Subtraction name='sub' geometry={objectsTrim} scale={[1, 1, 1]} />
      </Geometry>
      <meshLambertMaterial color={color} transparent={true} opacity={0.6} side={THREE.DoubleSide} />
    </mesh>
  )
});


export const MUpperSurfaceAp = memo(({
  oc,
  upperSurface,
  pointsTrim,
  callback,
  color,
  colorHigh,
  colorSub
}) => {
  const [objects, setObjects] = useState([]);
  const [objectsTrim, setObjectsTrim] = useState([]);
  const objectsSurface = [];
  var shapeSurfaces = new Array(6).fill(null);
  var key = 0;
  var cuttedSurface = null;
  var geomExtrude = null;
  var ref = useRef();


  useEffect(() => {
    console.log(upperSurface)

    if (upperSurface) {
      try {
        // Trim surface with opencascade from ML view //////////////////////////////////////////////
        if (pointsTrim == null) {
          return null;
        }
        console.log('MUpperSurfaceAp')

        const curve = new THREE.CatmullRomCurve3(pointsTrim, false, "catmullrom");
        const points = curve.getSpacedPoints(200);
        const tempCutShape = makeCutObjectAp(oc, points);

        const shape = new THREE.Shape();
        shape.moveTo(tempCutShape.points[0].x, tempCutShape.points[0].y)
        for (let i = 1; i < tempCutShape.points.length; i++) {
          shape.lineTo(tempCutShape.points[i].x, tempCutShape.points[i].y);
        }

        const extrudeSettings = {
          steps: 1,
          depth: 400,
        };

        geomExtrude = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        const meshExtrude = new THREE.Mesh(geomExtrude, material);

        objectsSurface.push(
          <primitive key={key} object={meshExtrude} />
        );
        key += 1;
      } catch (e) {
        console.log(e);
      }
    }

    setObjects(upperSurface);
    setObjectsTrim(geomExtrude);
    console.log('aaaaaa')
    const sleep = (waitTime) => {
      const startTime = Date.now();
      while (Date.now() - startTime < waitTime);
    };
    sleep(1000);

    try {
      var geom = ref.current.geometry
      console.log(geom)
      console.log(geom.getIndex())
      console.log(upperSurface.getAttribute("position"))
      console.log(geom.getAttribute("position"))
      const geomRepair = BufferGeometryUtils.mergeVertices(geom) // convert non-indexed buffergeometry to indexed buffergeometry. csg function convert the geometry to non-indexed.
      callback(geomRepair);
      toast.info('Finish trimming.')
    } catch (e) {
      console.log(e);
      var geom = ref.current.geometry
      //geom = BufferGeometryUtils.mergeVertices(geom)
      //callback(geom);
      //toast.error('Fail trimming.')
    }

  }, [upperSurface]);

  /*
  useEffect (() => {
    try {
      var geom = ref.current.geometry
      console.log(geom)
      console.log(geom.getIndex())
      console.log(geom.attributes)
      //const positionAttribute = ref.current.geometry.getAttribute("position").array
      geom = BufferGeometryUtils.mergeVertices(geom) // convert non-indexed buffergeometry to indexed buffergeometry. csg function convert the geometry to non-indexed.
      console.log(geom)
      callback(geom);
      toast.info('Finish trimming.')
    } catch (e) {
      console.log(e);
      callback(geom);
      toast.error('Fail trimming.')
    }
  }, [ref])
  */
  /*
  return objects.length !== 0 ? (
    <>
      {objects}
    </>
  ) : null;
  */

  return (
    <mesh >
      <Geometry ref={ref} computeVertexNormals>
        <Base name='base' geometry={objects} scale={[1, 1, 1]} />
        <Subtraction name='sub' geometry={objectsTrim} scale={[1, 1, 1]} />
      </Geometry>
      {/*
      <meshStandardMaterial color={color} transparent={true} opacity={0.4} side={THREE.DoubleSide}/>
      */}
      <meshLambertMaterial transparent={true} opacity={0.6} color={color} side={THREE.DoubleSide} />
    </mesh>
  )
});


export const MUpperCurve = memo(({
  oc,
  crossPoints,
  centroidBaseshape,
  greaterTrochanter,
  idxPointForm,
  p0X,
  p0Y,
  p1X,
  p1Y,
  p2X,
  p2Y,
  p3Y,
  p3CenterVecAmplify,
  p4Y,
  p4CenterVecAmplify,
  p5Y,
  p5CenterVecAmplify,
  p6Y,
  p6Z,
  p7Y,
  p7Z,
  p8Y,
  p8Z,
  p9Y,
  p9Z,
  p10Y,
  p10Z,
  p11Y,
  p11Z,
  p12Y,
  p12Z,
  p13Y,
  p13Z,
  lowerSurface,
  color,
  colorHigh,
  colorSub,
  callback
}) => {
  var objects = [];
  var objectsSub = [];
  var objectsSurface = [];
  var key = 0;
  var idxPoint = 0;
  var textPosition = [];

  const divCurvePoints = 15;

  //////// Greater trochanter curve ///////////////////////////////////////////
  var points0 = getGreaterTronchanterCurvePoints(crossPoints[crossPoints.length - 1], centroidBaseshape, greaterTrochanter, p0Y, p1Y, p2Y);
  points0 = modifyPoints(points0, p0X, p1X, p2X);
  var nurbsCurve = getNurbsCurve(points0);
  const points0Nurbs = nurbsCurve.getPoints(divCurvePoints).reverse();

  objects.push(<TubeCurve
    key={key}
    vpoints={points0Nurbs}
    radius={1}
    color={(idxPointForm === 0) || (idxPointForm === 1) || (idxPointForm === 2) ? colorHigh : color}
  />)
  key += 1;

  objectsSub.push(<Points
    key={key}
    vpoints={points0}
    size={2}
    color={colorSub}
  />)
  key += 1;

  for (let i = 0; i < points0.length - 1; i++) {
    textPosition = new THREE.Vector3(points0[i].x, points0[i].y, points0[i].z);

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }


  //////// Medial curve ///////////////////////////////////////////
  var temp = getMedialCurvePoints(crossPoints[crossPoints.length - 1], centroidBaseshape);
  var points1 = temp.points;
  const mlDiameter = temp.mlDiameter;

  var nurbsCurve = getNurbsCurve(points1);
  const points1Nurbs = nurbsCurve.getPoints(divCurvePoints);


  const lastVector = points1Nurbs[points1Nurbs.length - 1];
  const secondToLastVector = points1Nurbs[points1Nurbs.length - 2];

  const vectorLast = new THREE.Vector3().subVectors(lastVector, secondToLastVector).normalize();
  points1Nurbs.push(new THREE.Vector3(points1Nurbs[points1Nurbs.length - 1].x + vectorLast.x * 50, points1Nurbs[points1Nurbs.length - 1].y + vectorLast.y * 50, points1Nurbs[points1Nurbs.length - 1].z + vectorLast.z * 50));

  objects.push(<TubeCurve
    key={key}
    vpoints={points1Nurbs}
    radius={1}
    color={color}
  />)
  key += 1;

  //////// Back curve ///////////////////////////////////////////
  const baseshapeMlDividedPoints = getBaseshapeMlDividedPoints(crossPoints[crossPoints.length - 1], mlDiameter)
  var points2 = getBackCurvePoints(baseshapeMlDividedPoints, centroidBaseshape, p3Y, p3CenterVecAmplify, p4Y, p4CenterVecAmplify, p5Y, p5CenterVecAmplify);
  var nurbsCurve = getNurbsCurve(points2);
  const points2Nurbs = nurbsCurve.getPoints(divCurvePoints);

  objects.push(<TubeCurve
    key={key}
    vpoints={points2Nurbs}
    radius={1}
    color={(idxPointForm === 3) || (idxPointForm === 4) || (idxPointForm === 5) ? colorHigh : color}
  />)
  key += 1;

  for (let i = 1; i < 4; i++) {
    textPosition = points2[points2.length - i];

    objectsSub.push(<Points
      key={key}
      vpoints={points2}
      size={2}
      color={colorSub}
    />)
    key += 1;

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }


  //////// Lateral front curve ///////////////////////////////////////////
  var points3 = getLateralFrontCurvePoints(baseshapeMlDividedPoints, centroidBaseshape, p6Y, p6Z, p7Y, p7Z);
  var nurbsCurve = getNurbsCurve(points3);
  const points3Nurbs = nurbsCurve.getPoints(divCurvePoints);

  objects.push(<TubeCurve
    key={key}
    vpoints={points3Nurbs}
    radius={1}
    color={(idxPointForm === 6) || (idxPointForm === 7) ? colorHigh : color}
  />)
  key += 1;


  objectsSub.push(<Points
    key={key}
    vpoints={points3}
    size={2}
    color={colorSub}
  />)
  key += 1;

  for (let i = 0; i < points3.length; i++) {
    if (i === 0) {
      continue;
    }
    textPosition = new THREE.Vector3(points3[i].x, points3[i].y, points3[i].z);

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }

  //////// Medial front curve ///////////////////////////////////////////
  var points4 = getMedialFrontCurvePoints(baseshapeMlDividedPoints, centroidBaseshape, p8Y, p8Z, p9Y, p9Z);
  var nurbsCurve = getNurbsCurve(points4);
  const points4Nurbs = nurbsCurve.getPoints(divCurvePoints);

  objects.push(<TubeCurve
    key={key}
    vpoints={points4Nurbs}
    radius={1}
    color={(idxPointForm === 8) || (idxPointForm === 9) ? colorHigh : color}
  />)
  key += 1;


  objectsSub.push(<Points
    key={key}
    vpoints={points4}
    size={2}
    color={colorSub}
  />)
  key += 1;

  for (let i = 0; i < points4.length; i++) {
    if (i === 0) {
      continue;
    }
    textPosition = new THREE.Vector3(points4[i].x, points4[i].y, points4[i].z);

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }



  //////// Medial back curve start ///////////////////////////////////////////
  var points5 = getMedialBackCurvePoints(baseshapeMlDividedPoints, centroidBaseshape, p10Y, p10Z, p11Y, p11Z);
  var nurbsCurve = getNurbsCurve(points5);
  const points5Nurbs = nurbsCurve.getPoints(divCurvePoints);

  objects.push(<TubeCurve
    key={key}
    vpoints={points5Nurbs}
    radius={1}
    color={(idxPointForm === 10) || (idxPointForm === 11) ? colorHigh : color}
  />)
  key += 1;

  objectsSub.push(<Points
    key={key}
    vpoints={points5}
    size={2}
    color={colorSub}
  />)
  key += 1;

  for (let i = 0; i < points5.length; i++) {
    if (i === 0) {
      continue;
    }
    textPosition = new THREE.Vector3(points5[i].x, points5[i].y, points5[i].z);

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }

  //////// Medial back curve end ///////////////////////////////////////////
  var points6 = getMedialBackCurveEndPoints(baseshapeMlDividedPoints, centroidBaseshape, p12Y, p12Z, p13Y, p13Z);
  var nurbsCurve = getNurbsCurve(points6);
  const points6Nurbs = nurbsCurve.getPoints(divCurvePoints);

  objects.push(<TubeCurve
    key={key}
    vpoints={points6Nurbs}
    radius={1}
    color={(idxPointForm === 12) || (idxPointForm === 13) ? colorHigh : color}
  />)
  key += 1;

  objectsSub.push(<Points
    key={key}
    vpoints={points6}
    size={2}
    color={colorSub}
  />)
  key += 1;

  for (let i = 0; i < points6.length; i++) {
    if (i === 0) {
      continue;
    }
    textPosition = new THREE.Vector3(points6[i].x, points6[i].y, points6[i].z);

    objectsSub.push(<Text
      key={key}
      position={textPosition}
      rotation={[0, 0, 0]}
      color='white'
      fontSize={12}
      maxWidth={200} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      p{idxPoint}
    </Text>);
    key += 1;
    idxPoint += 1;
  }



  /*
  const temp0 = points0Nurbs.map((point) => {
    return new THREE.Vector4(point.x, point.y, point.z, 1);
  })
  const temp1 = points1Nurbs.map((point) => {
    return new THREE.Vector4(point.x, point.y, point.z, 1);
  })

  
  const nsControlPoints = [
    temp0,
    [
      new THREE.Vector4( 100, 0, -20, 1 ),
      new THREE.Vector4( 100, 0, 20, 1 ),
      new THREE.Vector4( 100, 0, 40, 1 ),
      new THREE.Vector4( 100, 0, 60, 1 )
    ],
    temp1
  ];
  */

  /*
  // NURBS surface
  const nsControlPoints = [
    [
      new THREE.Vector4( - 200, - 200, 100, 1 ),
      new THREE.Vector4( - 200, - 100, - 200, 1 ),
      new THREE.Vector4( - 200, 100, 250, 1 ),
      new THREE.Vector4( - 200, 200, - 100, 1 )
    ],
    [
      new THREE.Vector4( 100, - 100, 0, 1 ),
      new THREE.Vector4( 100, - 100, - 100, 1 ),
      new THREE.Vector4( 100, 100, 150, 1 ),
      new THREE.Vector4( 100, 200, 0, 1 )
    ],
    [
      new THREE.Vector4( 200, - 200, - 100, 1 ),
      new THREE.Vector4( 200, - 100, 200, 1 ),
      new THREE.Vector4( 200, 100, - 250, 1 ),
      new THREE.Vector4( 200, 200, 100, 1 )
    ]
  ];
  
  const degree1 = 2;
  const degree2 = 3;
  const knots1 = [ 0, 0, 0, 1, 1, 1 ];
  const knots2 = [ 0, 0, 0, 0, 1, 1, 1, 1 ];
  const nurbsSurface = new NURBSSurface( degree1, degree2, knots1, knots2, nsControlPoints );

  function getSurfacePoint( u, v, target ) {
    return nurbsSurface.getPoint( u, v, target );
  }
  const geometry = new ParametricGeometry( getSurfacePoint, 20, 20 );
  const material = new THREE.MeshBasicMaterial({ color: 0x6699FF, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geometry, material);
  //objects.push(
  //  <primitive object={mesh} />
  //)
  //key += 1;
  */

  const upperCurvePointsAll = [];
  upperCurvePointsAll.push(points0Nurbs);
  upperCurvePointsAll.push(points2Nurbs);
  upperCurvePointsAll.push(points6Nurbs);
  upperCurvePointsAll.push(points5Nurbs);
  upperCurvePointsAll.push(points1Nurbs);
  upperCurvePointsAll.push(points4Nurbs);
  upperCurvePointsAll.push(points3Nurbs);

  if (upperCurvePointsAll.length !== 0) {
    callback(upperCurvePointsAll);
  }

  return objects.length !== 0 ? (
    <React.Fragment>
      {objects}
      {objectsSub}

    </React.Fragment>
  ) : null;
});
